/* eslint-disable no-bitwise, no-continue, no-param-reassign, @typescript-eslint/no-use-before-define */
// Low-level raster/EXIF byte parser: bitwise ops, `continue`, in-place buffer
// mutation, and forward function references are inherent to it and allowed here.
/**
 * Extracts embedded ICC / EXIF / resolution metadata from a source raster file and
 * grafts it onto a canvas-reencoded destination so upload bytes stay print-faithful.
 *
 * Regenerate `app/assets/javascripts/shared_js/preserve_raster_image_metadata.js`:
 * npx esbuild app/frontend/packages/files_uploader/core/preserve-raster-image-metadata.ts \
 *   --bundle --format=iife --global-name=PreserveRasterImageMetadata \
 *   --outfile=app/assets/javascripts/shared_js/preserve_raster_image_metadata.js \
 *   --platform=browser
 */

const PNG_SIGNATURE = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]);

const PRESERVE_PNG_CHUNK_TYPES = new Set(["iCCP", "eXIf", "pHYs", "tIME"]);

const PRESERVE_WEBP_CHUNK_FOURCCS = new Set(["ICCP", "EXIF", "XMP "]);

export type RasterImageFormat = "jpeg" | "png" | "webp" | "bmp";

export const detectRasterImageFormat = (
  mimeType: string,
  bytes: Uint8Array
): RasterImageFormat | null => {
  const mime = mimeType.toLowerCase();

  if (/^image\/(jpeg|pjpeg)$/.test(mime)) {
    return "jpeg";
  }

  if (/^image\/png$/.test(mime)) {
    return "png";
  }

  if (/^image\/webp$/.test(mime)) {
    return "webp";
  }

  if (/^image\/(bmp|x-ms-bmp)$/.test(mime)) {
    return "bmp";
  }

  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xd8) {
    return "jpeg";
  }

  if (
    bytes.length >= 8 &&
    PNG_SIGNATURE.every((value, index) => bytes[index] === value)
  ) {
    return "png";
  }

  if (
    bytes.length >= 12 &&
    readAscii(bytes, 0, 4) === "RIFF" &&
    readAscii(bytes, 8, 4) === "WEBP"
  ) {
    return "webp";
  }

  if (bytes.length >= 2 && bytes[0] === 0x42 && bytes[1] === 0x4d) {
    return "bmp";
  }

  return null;
};

export const mergeUploadMetadata = async (
  sourceFile: File,
  compressedFile: File
): Promise<File> => {
  const sourceBytes = new Uint8Array(await sourceFile.arrayBuffer());
  const compressedBytes = new Uint8Array(await compressedFile.arrayBuffer());
  const mergedBytes = preserveRasterImageMetadata(
    sourceBytes,
    compressedBytes,
    sourceFile.type
  );

  return new File([mergedBytes], compressedFile.name, {
    type: compressedFile.type,
    lastModified: compressedFile.lastModified,
  });
};

export const preserveRasterImageMetadata = (
  sourceBytes: Uint8Array,
  compressedBytes: Uint8Array,
  mimeType: string
): Uint8Array => {
  const format = detectRasterImageFormat(mimeType, sourceBytes);

  if (!format) {
    return compressedBytes;
  }

  const compressedFormat = detectRasterImageFormat(mimeType, compressedBytes);

  if (compressedFormat !== format) {
    return compressedBytes;
  }

  switch (format) {
    case "jpeg":
      return mergeJpegMetadata(sourceBytes, compressedBytes);
    case "png":
      return mergePngMetadata(sourceBytes, compressedBytes);
    case "webp":
      return mergeWebpMetadata(sourceBytes, compressedBytes);
    case "bmp":
      return mergeBmpMetadata(sourceBytes, compressedBytes);
    default:
      return compressedBytes;
  }
};

const readAscii = (bytes: Uint8Array, offset: number, length: number): string =>
  String.fromCharCode(...bytes.subarray(offset, offset + length));

const readUint32Le = (bytes: Uint8Array, offset: number): number =>
  (bytes[offset] ?? 0) |
  ((bytes[offset + 1] ?? 0) << 8) |
  ((bytes[offset + 2] ?? 0) << 16) |
  ((bytes[offset + 3] ?? 0) << 24);

const writeUint32Le = (
  bytes: Uint8Array,
  offset: number,
  value: number
): void => {
  bytes[offset] = value & 0xff;
  bytes[offset + 1] = (value >> 8) & 0xff;
  bytes[offset + 2] = (value >> 16) & 0xff;
  bytes[offset + 3] = (value >> 24) & 0xff;
};

const concatUint8Arrays = (parts: Uint8Array[]): Uint8Array => {
  const totalLength = parts.reduce((sum, part) => sum + part.length, 0);
  const merged = new Uint8Array(totalLength);
  let offset = 0;

  for (const part of parts) {
    merged.set(part, offset);
    offset += part.length;
  }

  return merged;
};

interface JpegSegment {
  marker: number;
  raw: Uint8Array;
}

const parseJpegBeforeSos = (
  bytes: Uint8Array
): { preamble: JpegSegment[]; sosOffset: number } => {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) {
    throw new Error("Not a valid JPEG");
  }

  let offset = 2;
  const preamble: JpegSegment[] = [];

  while (offset + 1 < bytes.length) {
    if (bytes[offset] !== 0xff) {
      throw new Error("Invalid JPEG marker");
    }

    const marker = bytes[offset + 1] ?? 0;

    if (
      marker === 0xd8 ||
      (marker >= 0xd0 && marker <= 0xd7) ||
      marker === 0xd9
    ) {
      offset += 2;
      continue;
    }

    if (marker === 0xda) {
      return { preamble, sosOffset: offset };
    }

    if (offset + 3 >= bytes.length) {
      throw new Error("Truncated JPEG segment");
    }

    const segmentLength =
      ((bytes[offset + 2] ?? 0) << 8) | (bytes[offset + 3] ?? 0);
    const segmentEnd = offset + 2 + segmentLength;

    if (segmentEnd > bytes.length) {
      throw new Error("JPEG segment exceeds file size");
    }

    preamble.push({
      marker,
      raw: bytes.subarray(offset, segmentEnd),
    });

    offset = segmentEnd;
  }

  throw new Error("JPEG missing SOS marker");
};

const isApp0JfifSegment = (segment: JpegSegment): boolean =>
  segment.marker === 0xe0 &&
  segment.raw.length >= 9 &&
  readAscii(segment.raw, 4, 5) === "JFIF\x00";

const isApp1ExifSegment = (segment: JpegSegment): boolean =>
  segment.marker === 0xe1 &&
  segment.raw.length >= 6 &&
  readAscii(segment.raw, 4, 4) === "Exif";

const XMP_NAMESPACE_HEADER = "http://ns.adobe.com/xap/1.0/\x00";

// APP1 XMP packet (dc:title / dc:description / dc:subject etc.). Photoshop
// writes the descriptive "File Info" fields here; dropping it on re-encode is
// what broke artwork metadata auto-populate for compressed uploads (ART-8415).
const isApp1XmpSegment = (segment: JpegSegment): boolean =>
  segment.marker === 0xe1 &&
  segment.raw.length >= 4 + XMP_NAMESPACE_HEADER.length &&
  readAscii(segment.raw, 4, XMP_NAMESPACE_HEADER.length) ===
    XMP_NAMESPACE_HEADER;

const PHOTOSHOP_IRB_HEADER = "Photoshop 3.0\x00";

// APP13 Photoshop IRB block — carries the IPTC record (By-line, ObjectName,
// Caption-Abstract, Keywords). Same ART-8415 motivation as the XMP segment.
const isApp13PhotoshopSegment = (segment: JpegSegment): boolean =>
  segment.marker === 0xed &&
  segment.raw.length >= 4 + PHOTOSHOP_IRB_HEADER.length &&
  readAscii(segment.raw, 4, PHOTOSHOP_IRB_HEADER.length) ===
    PHOTOSHOP_IRB_HEADER;

const isIccProfilePayload = (payload: Uint8Array): boolean =>
  payload.length >= 12 &&
  readAscii(payload, 0, 11) === "ICC_PROFILE" &&
  payload[11] === 0;

const isApp2IccSegment = (segment: JpegSegment): boolean =>
  segment.marker === 0xe2 &&
  segment.raw.length >= 16 &&
  isIccProfilePayload(segment.raw.subarray(4));

const isReplaceableJpegAppSegment = (segment: JpegSegment): boolean =>
  isApp0JfifSegment(segment) ||
  isApp1ExifSegment(segment) ||
  isApp1XmpSegment(segment) ||
  isApp13PhotoshopSegment(segment) ||
  isApp2IccSegment(segment);

const fixExifOrientationInApp1Segment = (segment: Uint8Array): Uint8Array => {
  const copy = segment.slice();
  const tiffOffset = 10;
  const tiffLimit = copy.length;

  if (tiffLimit < tiffOffset + 8) {
    return copy;
  }

  let littleEndian = false;

  if (copy[tiffOffset] === 0x49 && copy[tiffOffset + 1] === 0x49) {
    littleEndian = true;
  } else if (copy[tiffOffset] === 0x4d && copy[tiffOffset + 1] === 0x4d) {
    littleEndian = false;
  } else {
    return copy;
  }

  const readUint16 = (offset: number): number =>
    littleEndian
      ? (copy[offset] ?? 0) | ((copy[offset + 1] ?? 0) << 8)
      : ((copy[offset] ?? 0) << 8) | (copy[offset + 1] ?? 0);

  const readUint32 = (offset: number): number => {
    if (littleEndian) {
      return readUint32Le(copy, offset);
    }

    return (
      ((copy[offset] ?? 0) << 24) |
      ((copy[offset + 1] ?? 0) << 16) |
      ((copy[offset + 2] ?? 0) << 8) |
      (copy[offset + 3] ?? 0)
    );
  };

  const writeUint16 = (offset: number, value: number): void => {
    if (littleEndian) {
      copy[offset] = value & 0xff;
      copy[offset + 1] = (value >> 8) & 0xff;
    } else {
      copy[offset] = (value >> 8) & 0xff;
      copy[offset + 1] = value & 0xff;
    }
  };

  if (readUint16(tiffOffset + 2) !== 0x002a) {
    return copy;
  }

  const ifd0Offset = readUint32(tiffOffset + 4);
  const ifd0Start = tiffOffset + ifd0Offset;

  if (ifd0Start + 2 > tiffLimit) {
    return copy;
  }

  const entryCount = readUint16(ifd0Start);
  const entriesStart = ifd0Start + 2;

  for (let index = 0; index < entryCount; index += 1) {
    const entryOffset = entriesStart + index * 12;

    if (entryOffset + 12 > tiffLimit) {
      break;
    }

    if (readUint16(entryOffset) !== 0x0112) {
      continue;
    }

    if (
      readUint16(entryOffset + 2) !== 3 ||
      readUint32(entryOffset + 4) !== 1
    ) {
      break;
    }

    writeUint16(entryOffset + 8, 1);
    break;
  }

  return copy;
};

const extractJpegMetadataSegments = (sourceBytes: Uint8Array): Uint8Array[] => {
  const { preamble } = parseJpegBeforeSos(sourceBytes);
  const preserved: Uint8Array[] = [];

  for (const segment of preamble) {
    if (isApp0JfifSegment(segment)) {
      preserved.push(segment.raw);
    } else if (isApp1ExifSegment(segment)) {
      preserved.push(fixExifOrientationInApp1Segment(segment.raw));
    } else if (isApp1XmpSegment(segment) || isApp13PhotoshopSegment(segment)) {
      preserved.push(segment.raw);
    } else if (isApp2IccSegment(segment)) {
      preserved.push(segment.raw);
    }
  }

  return preserved;
};

const mergeJpegMetadata = (
  sourceBytes: Uint8Array,
  compressedBytes: Uint8Array
): Uint8Array => {
  const preserved = extractJpegMetadataSegments(sourceBytes);
  const { preamble, sosOffset } = parseJpegBeforeSos(compressedBytes);

  const imageSegments = preamble
    .filter((segment) => !isReplaceableJpegAppSegment(segment))
    .map((segment) => segment.raw);

  return concatUint8Arrays([
    new Uint8Array([0xff, 0xd8]),
    ...preserved,
    ...imageSegments,
    compressedBytes.subarray(sosOffset),
  ]);
};

interface PngChunk {
  type: string;
  raw: Uint8Array;
}

const parsePngChunks = (bytes: Uint8Array): PngChunk[] => {
  if (
    bytes.length < PNG_SIGNATURE.length ||
    !PNG_SIGNATURE.every((value, index) => bytes[index] === value)
  ) {
    throw new Error("Not a valid PNG");
  }

  const chunks: PngChunk[] = [];
  let offset = PNG_SIGNATURE.length;

  while (offset + 12 <= bytes.length) {
    const length = readUint32Be(bytes, offset);
    const type = readAscii(bytes, offset + 4, 4);
    const chunkEnd = offset + 12 + length;

    if (chunkEnd > bytes.length) {
      throw new Error("PNG chunk exceeds file size");
    }

    chunks.push({
      type,
      raw: bytes.subarray(offset, chunkEnd),
    });

    offset = chunkEnd;

    if (type === "IEND") {
      break;
    }
  }

  return chunks;
};

const readUint32Be = (bytes: Uint8Array, offset: number): number =>
  ((bytes[offset] ?? 0) << 24) |
  ((bytes[offset + 1] ?? 0) << 16) |
  ((bytes[offset + 2] ?? 0) << 8) |
  (bytes[offset + 3] ?? 0);

const extractPngMetadataChunks = (sourceBytes: Uint8Array): PngChunk[] =>
  parsePngChunks(sourceBytes).filter((chunk) =>
    PRESERVE_PNG_CHUNK_TYPES.has(chunk.type)
  );

const mergePngMetadata = (
  sourceBytes: Uint8Array,
  compressedBytes: Uint8Array
): Uint8Array => {
  const preserved = extractPngMetadataChunks(sourceBytes);
  const destChunks = parsePngChunks(compressedBytes).filter(
    (chunk) => !PRESERVE_PNG_CHUNK_TYPES.has(chunk.type)
  );

  const mergedChunks: Uint8Array[] = [PNG_SIGNATURE.slice()];

  for (const chunk of destChunks) {
    mergedChunks.push(chunk.raw);

    if (chunk.type === "IHDR") {
      for (const preservedChunk of preserved) {
        mergedChunks.push(preservedChunk.raw);
      }
    }
  }

  return concatUint8Arrays(mergedChunks);
};

interface WebpChunk {
  fourcc: string;
  raw: Uint8Array;
}

const parseWebpChunks = (bytes: Uint8Array): WebpChunk[] => {
  if (
    bytes.length < 12 ||
    readAscii(bytes, 0, 4) !== "RIFF" ||
    readAscii(bytes, 8, 4) !== "WEBP"
  ) {
    throw new Error("Not a valid WebP");
  }

  const chunks: WebpChunk[] = [];
  const riffEnd = Math.min(bytes.length, 8 + readUint32Le(bytes, 4));
  let offset = 12;

  while (offset + 8 <= riffEnd) {
    const fourcc = readAscii(bytes, offset, 4);
    const size = readUint32Le(bytes, offset + 4);
    const paddedSize = size + (size % 2);
    const chunkEnd = offset + 8 + paddedSize;

    if (chunkEnd > bytes.length) {
      throw new Error("WebP chunk exceeds file size");
    }

    chunks.push({
      fourcc,
      raw: bytes.subarray(offset, chunkEnd),
    });

    offset = chunkEnd;
  }

  return chunks;
};

const extractWebpMetadataChunks = (sourceBytes: Uint8Array): WebpChunk[] =>
  parseWebpChunks(sourceBytes).filter((chunk) =>
    PRESERVE_WEBP_CHUNK_FOURCCS.has(chunk.fourcc)
  );

const mergeWebpMetadata = (
  sourceBytes: Uint8Array,
  compressedBytes: Uint8Array
): Uint8Array => {
  const preserved = extractWebpMetadataChunks(sourceBytes);
  const destChunks = parseWebpChunks(compressedBytes).filter(
    (chunk) => !PRESERVE_WEBP_CHUNK_FOURCCS.has(chunk.fourcc)
  );

  const chunkBytes: Uint8Array[] = [];
  let metadataInserted = false;

  for (const chunk of destChunks) {
    chunkBytes.push(chunk.raw);

    if (!metadataInserted && chunk.fourcc === "VP8X") {
      for (const preservedChunk of preserved) {
        chunkBytes.push(preservedChunk.raw);
      }

      metadataInserted = true;
    }
  }

  if (!metadataInserted && preserved.length > 0) {
    return compressedBytes;
  }

  const webpPayload = concatUint8Arrays(chunkBytes);
  const riffSize = 4 + webpPayload.length;
  const header = new Uint8Array(12);

  writeAscii(header, 0, "RIFF");
  writeUint32Le(header, 4, riffSize);
  writeAscii(header, 8, "WEBP");

  return concatUint8Arrays([header, webpPayload]);
};

const writeAscii = (bytes: Uint8Array, offset: number, value: string): void => {
  for (let index = 0; index < value.length; index += 1) {
    bytes[offset + index] = value.charCodeAt(index);
  }
};

interface BmpMetadata {
  dibSize: number;
  pixelsPerMeterX: number | null;
  pixelsPerMeterY: number | null;
  colorProfile: Uint8Array | null;
}

const extractBmpMetadata = (bytes: Uint8Array): BmpMetadata | null => {
  if (bytes.length < 14 || bytes[0] !== 0x42 || bytes[1] !== 0x4d) {
    return null;
  }

  const dibOffset = 14;

  if (dibOffset + 4 > bytes.length) {
    return null;
  }

  const dibSize = readUint32Le(bytes, dibOffset);
  let pixelsPerMeterX: number | null = null;
  let pixelsPerMeterY: number | null = null;
  let colorProfile: Uint8Array | null = null;

  if (dibSize >= 40 && dibOffset + 32 <= bytes.length) {
    pixelsPerMeterX = readUint32Le(bytes, dibOffset + 24);
    pixelsPerMeterY = readUint32Le(bytes, dibOffset + 28);
  }

  if (dibSize >= 124 && dibOffset + 125 <= bytes.length) {
    const profileOffset = readUint32Le(bytes, dibOffset + 117);
    const profileSize = readUint32Le(bytes, dibOffset + 121);

    if (profileSize > 0 && profileOffset + profileSize <= bytes.length) {
      colorProfile = bytes.subarray(profileOffset, profileOffset + profileSize);
    }
  }

  return {
    dibSize,
    pixelsPerMeterX,
    pixelsPerMeterY,
    colorProfile,
  };
};

const mergeBmpMetadata = (
  sourceBytes: Uint8Array,
  compressedBytes: Uint8Array
): Uint8Array => {
  const metadata = extractBmpMetadata(sourceBytes);

  if (!metadata) {
    return compressedBytes;
  }

  const merged = compressedBytes.slice();
  const dibOffset = 14;

  if (merged.length < dibOffset + 4) {
    return compressedBytes;
  }

  const destDibSize = readUint32Le(merged, dibOffset);

  if (
    metadata.pixelsPerMeterX !== null &&
    metadata.pixelsPerMeterY !== null &&
    destDibSize >= 40 &&
    dibOffset + 32 <= merged.length
  ) {
    writeUint32Le(merged, dibOffset + 24, metadata.pixelsPerMeterX);
    writeUint32Le(merged, dibOffset + 28, metadata.pixelsPerMeterY);
  }

  if (
    !metadata.colorProfile ||
    metadata.colorProfile.length === 0 ||
    destDibSize < 124 ||
    dibOffset + 125 > merged.length
  ) {
    return merged;
  }

  const existingProfileOffset = readUint32Le(merged, dibOffset + 117);
  const existingProfileSize = readUint32Le(merged, dibOffset + 121);

  if (
    existingProfileSize > 0 &&
    existingProfileOffset + existingProfileSize === merged.length &&
    existingProfileSize === metadata.colorProfile.length
  ) {
    merged.set(metadata.colorProfile, existingProfileOffset);

    return merged;
  }

  if (existingProfileSize === 0) {
    const profileOffset = merged.length;
    const withProfile = concatUint8Arrays([merged, metadata.colorProfile]);

    writeUint32Le(withProfile, dibOffset + 117, profileOffset);
    writeUint32Le(withProfile, dibOffset + 121, metadata.colorProfile.length);

    return withProfile;
  }

  return merged;
};
